/**
 * Financial Module Page
 * Sprint 12 - Financial Module MVP
 * 
 * Unified page for financial estimation, baselines, and variance tracking
 * Integrates FM-1, FM-2, FM-3 components
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DollarSign, GitCommit, TrendingUp, Calculator, BarChart3,
  ChevronDown, RefreshCw, Loader2, Info, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import FinancialEstimateTab from '../components/FinancialEstimateTab';
import FinancialBaselineHistory from '../components/FinancialBaselineHistory';
import BenefitsTrendingChart from '../components/BenefitsTrendingChart';
import { useCurrency } from '../utils/currencyUtils';

// API client for financial module
const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

const apiGet = async (url, params = {}) => {
  return axios.get(`${API_BASE}${url}`, { headers: getAuthHeaders(), params });
};

// Tabs configuration
const TABS = [
  { id: 'estimate', label: 'Estimate', icon: Calculator, description: 'Define costs and benefits' },
  { id: 'baselines', label: 'Baselines', icon: GitCommit, description: 'Version history' },
  { id: 'variance', label: 'Variance', icon: TrendingUp, description: 'Track changes' },
  { id: 'trending', label: 'Benefits Trend', icon: BarChart3, description: 'Benefits over time' },
];

const FinancialModulePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, tenantId: authTenantId } = useAuth();
  const { formatCurrency, currencySymbol, currencyCode } = useCurrency();
  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'estimate');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState(null);
  const [currentBaseline, setCurrentBaseline] = useState(null);
  const [variances, setVariances] = useState([]);

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await apiGet('/projects', { tenant_id: tenantId, limit: 100 });
        const projectList = res.data.projects || [];
        setProjects(projectList);
        
        // Auto-select first project or from URL
        const projectId = searchParams.get('projectId');
        if (projectId) {
          const found = projectList.find(p => p.id === projectId);
          setSelectedProject(found || projectList[0]);
        } else if (projectList.length > 0) {
          setSelectedProject(projectList[0]);
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
        toast.error('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    
    loadProjects();
  }, [tenantId, searchParams]);

  // Load scopes when project changes
  useEffect(() => {
    const loadScopes = async () => {
      if (!selectedProject?.id) {
        setScopes([]);
        setSelectedScope(null);
        return;
      }
      
      try {
        // Use scoping API with tenant_id and project_id query params
        const res = await apiGet(`/scoping/scopes`, { 
          tenant_id: tenantId, 
          project_id: selectedProject.id 
        });
        const scopeList = res.data.scopes || res.data || [];
        setScopes(scopeList);
        
        // Auto-select first scope or from URL
        const scopeId = searchParams.get('scopeId');
        if (scopeId) {
          const found = scopeList.find(s => s.id === scopeId || s._id === scopeId);
          setSelectedScope(found || (scopeList.length > 0 ? scopeList[0] : null));
        } else if (scopeList.length > 0) {
          setSelectedScope(scopeList[0]);
        } else {
          setSelectedScope(null);
        }
      } catch (err) {
        console.error('Failed to load scopes:', err);
        setScopes([]);
        setSelectedScope(null);
      }
    };
    
    loadScopes();
  }, [selectedProject, searchParams]);

  // Load current baseline and variances when scope changes
  useEffect(() => {
    const loadFinancialData = async () => {
      if (!selectedScope?.id && !selectedScope?._id) {
        setCurrentBaseline(null);
        setVariances([]);
        return;
      }
      
      const scopeId = selectedScope.id || selectedScope._id;
      
      try {
        // Load current baseline
        const baselineRes = await apiGet(`/financial/baseline/current?scopeId=${scopeId}`);
        setCurrentBaseline(baselineRes.data.baseline);
        
        // Load variances
        const varianceRes = await apiGet(`/financial/variances?scopeId=${scopeId}`);
        setVariances(varianceRes.data.variances || []);
      } catch (err) {
        console.error('Failed to load financial data:', err);
        setCurrentBaseline(null);
        setVariances([]);
      }
    };
    
    loadFinancialData();
  }, [selectedScope]);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(prev => {
      prev.set('tab', tabId);
      return prev;
    });
  };

  // Check if user has PMO/Admin role
  const canEdit = ['admin', 'owner', 'pmo_power', 'pmo_analyst'].includes(user?.role);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const scopeId = selectedScope?.id || selectedScope?._id;

  return (
    <PageErrorBoundary pageName="Financial Module">
      <div className="min-h-screen bg-gray-50" data-testid="financial-module-page">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <DollarSign className="w-7 h-7 text-green-600" />
                  Financial Module
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Estimate costs, track baselines, and monitor variances
                </p>
              </div>
              
              {/* Selectors */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Project:</label>
                  <select
                    value={selectedProject?.id || ''}
                    onChange={(e) => {
                      const project = projects.find(p => p.id === e.target.value);
                      setSelectedProject(project);
                      setSearchParams(prev => {
                        prev.set('projectId', e.target.value);
                        prev.delete('scopeId');
                        return prev;
                      });
                    }}
                    className="px-3 py-2 border rounded-lg text-sm min-w-[200px]"
                    data-testid="project-selector"
                  >
                    <option value="">Select project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Scope:</label>
                  <select
                    value={scopeId || ''}
                    onChange={(e) => {
                      const scope = scopes.find(s => (s.id || s._id) === e.target.value);
                      setSelectedScope(scope);
                      setSearchParams(prev => {
                        prev.set('scopeId', e.target.value);
                        return prev;
                      });
                    }}
                    className="px-3 py-2 border rounded-lg text-sm min-w-[200px]"
                    disabled={scopes.length === 0}
                    data-testid="scope-selector"
                  >
                    <option value="">
                      {scopes.length === 0 ? 'No scopes available' : 'Select scope'}
                    </option>
                    {scopes.map(s => (
                      <option key={s.id || s._id} value={s.id || s._id}>
                        {s.name || `Scope ${s.version || 'Draft'}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Current Baseline Summary */}
            {currentBaseline && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GitCommit className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-medium text-green-800">
                      Current Baseline: V{currentBaseline.version}
                    </span>
                    <span className="text-sm text-green-600 ml-2">
                      {formatCurrency(currentBaseline.total_cost)} total cost
                    </span>
                  </div>
                </div>
                <div className="text-sm text-green-700">
                  ROI: {currentBaseline.roi_percentage?.toFixed(1)}% | 
                  Payback: {currentBaseline.payback_months} months
                </div>
              </div>
            )}
          </div>
          
          {/* Tabs */}
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-1 border-t pt-2">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                      isActive
                        ? 'text-green-700 border-green-600 bg-green-50'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto">
          <WidgetErrorBoundary widgetName="Financial Content">
            {!selectedProject && (
              <div className="p-12 text-center text-gray-500">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="font-medium text-lg">Select a Project</p>
                <p className="text-sm mt-1">Choose a project to view and manage financial data</p>
              </div>
            )}
            
            {selectedProject && !scopeId && (
              <div className="p-12 text-center text-gray-500">
                <Calculator className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="font-medium text-lg">No Scope Selected</p>
                <p className="text-sm mt-1">
                  {scopes.length === 0
                    ? 'This project has no scopes. Create a scope first.'
                    : 'Select a scope to manage financial estimates.'}
                </p>
              </div>
            )}
            
            {scopeId && activeTab === 'estimate' && (
              <FinancialEstimateTab
                scopeId={scopeId}
                projectId={selectedProject?.id}
                readOnly={!canEdit}
              />
            )}
            
            {scopeId && activeTab === 'baselines' && (
              <FinancialBaselineHistory 
                scopeId={scopeId} 
                onNavigateToVariance={() => handleTabChange('variance')}
              />
            )}
            
            {scopeId && activeTab === 'variance' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Variance Tracking
                  </h2>
                  {/* Latest Variance Badge */}
                  {variances.length > 0 && (
                    <div className={`px-4 py-2 rounded-lg font-semibold text-lg ${
                      variances[variances.length - 1]?.variance_percentage >= 0
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      Latest: {variances[variances.length - 1]?.variance_percentage >= 0 ? '+' : ''}
                      {variances[variances.length - 1]?.variance_percentage?.toFixed(1)}%
                    </div>
                  )}
                </div>
                
                {variances.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No Variances Recorded</p>
                    <p className="text-sm mt-1">
                      Variances are tracked when decisions impact the financial baseline
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Top 3 Contributing Categories */}
                    {(() => {
                      const latestV = variances[variances.length - 1];
                      if (!latestV) return null;
                      
                      const categories = [
                        { label: 'Labor', delta: Math.abs(latestV.delta_labor || 0), value: latestV.delta_labor || 0 },
                        { label: 'Non-Labor', delta: Math.abs(latestV.delta_non_labor || 0), value: latestV.delta_non_labor || 0 },
                        { label: 'CAPEX', delta: Math.abs(latestV.delta_capex || 0), value: latestV.delta_capex || 0 },
                        { label: 'OPEX', delta: Math.abs(latestV.delta_opex || 0), value: latestV.delta_opex || 0 }
                      ].sort((a, b) => b.delta - a.delta).slice(0, 3);
                      
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="text-sm font-semibold text-blue-800 mb-3">Top 3 Contributing Categories (Latest)</div>
                          <div className="grid grid-cols-3 gap-4">
                            {categories.map((cat, i) => (
                              <div key={cat.label} className="text-center">
                                <div className="text-xs text-gray-500 mb-1">#{i + 1} {cat.label}</div>
                                <div className={`font-bold ${cat.value >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {cat.value >= 0 ? '+' : ''}{formatCurrency(cat.value)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Variance History */}
                    {variances.map((v, idx) => (
                      <div key={idx} className={`bg-white border rounded-lg p-4 ${
                        idx === variances.length - 1 ? 'ring-2 ring-blue-500' : ''
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              v.delta_cost >= 0 ? 'bg-red-100' : 'bg-green-100'
                            }`}>
                              {v.delta_cost >= 0 ? (
                                <TrendingUp className="w-5 h-5 text-red-600" />
                              ) : (
                                <TrendingUp className="w-5 h-5 text-green-600 transform rotate-180" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 flex items-center gap-2">
                                V{v.from_version} → V{v.to_version}
                                {idx === variances.length - 1 && (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">LATEST</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                Decision: {v.decision_id || 'N/A'}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-xl font-bold ${
                              v.delta_cost >= 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {v.delta_cost >= 0 ? '+' : ''}{formatCurrency(v.delta_cost)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {v.variance_percentage?.toFixed(1)}% variance
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 text-sm pt-3 border-t">
                          <div>
                            <span className="text-gray-500">ΔCAPEX:</span>
                            <span className={v.delta_capex >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {' '}{v.delta_capex >= 0 ? '+' : ''}{formatCurrency(v.delta_capex)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">ΔOPEX:</span>
                            <span className={v.delta_opex >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {' '}{v.delta_opex >= 0 ? '+' : ''}{formatCurrency(v.delta_opex)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">ΔLabor:</span>
                            <span className={v.delta_labor >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {' '}{v.delta_labor >= 0 ? '+' : ''}{formatCurrency(v.delta_labor)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">ΔNon-Labor:</span>
                            <span className={v.delta_non_labor >= 0 ? 'text-red-600' : 'text-green-600'}>
                              {' '}{v.delta_non_labor >= 0 ? '+' : ''}{formatCurrency(v.delta_non_labor)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Total Variance Summary */}
                    <div className="bg-gray-100 rounded-lg p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">Total Cumulative Variance</span>
                        <span className={`text-xl font-bold ${
                          variances.reduce((sum, v) => sum + (v.delta_cost || 0), 0) >= 0
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}>
                          {formatCurrency(variances.reduce((sum, v) => sum + (v.delta_cost || 0), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {scopeId && activeTab === 'trending' && (
              <div className="p-6">
                <BenefitsTrendingChart 
                  scopeId={scopeId} 
                  projectId={selectedProject?.id}
                />
              </div>
            )}
          </WidgetErrorBoundary>
        </div>

        {/* Access Warning for non-PMO users */}
        {!canEdit && scopeId && (
          <div className="fixed bottom-4 right-4 bg-amber-100 border border-amber-300 rounded-lg p-3 flex items-center gap-2 shadow-lg">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-amber-800">Read-only mode - PMO/Admin role required to edit</span>
          </div>
        )}
      </div>
    </PageErrorBoundary>
  );
};

export default FinancialModulePage;
